/**
 * NewsletterCTA Component
 * Newsletter signup call-to-action
 */

'use client';

import { useState } from 'react';
import { trackNewsletterSignup } from '@/lib/analytics';
import { functions, httpsCallable } from '@/lib/firebase.client';

export default function NewsletterCTA() {
  const [email, setEmail] = useState('');
  const [cadence, setCadence] = useState<'weekly' | 'monthly'>('monthly');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const subscribeNewsletterCallable = httpsCallable(functions, 'subscribeNewsletter');
      const response = await subscribeNewsletterCallable({ email, cadence });
      const data = response.data as { success: boolean; error?: string };

      if (data.success) {
        setSuccess(true);
        setEmail('');
        trackNewsletterSignup(cadence);
      } else {
        setError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl shadow-lg p-8 text-center animate-fade-in">
        <div className="text-6xl mb-4">🎉</div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">You're subscribed!</h3>
        <p className="text-gray-700 mb-4">
          Check your email for a confirmation. You can create an account to manage your preferences.
        </p>
        <a
          href="/auth/signup"
          className="inline-block bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
        >
          Create Account
        </a>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl shadow-lg p-8">
      <div className="max-w-2xl mx-auto text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Stay Updated on FSA/HSA Eligible Items
        </h3>
        <p className="text-gray-700 mb-6">
          Get curated lists, trending searches, and seasonal tips delivered to your inbox.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
            />
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value as 'weekly' | 'monthly')}
              className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 outline-none bg-white text-gray-900 font-medium"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Subscribing...' : 'Subscribe'}
          </button>

          <p className="text-xs text-gray-600">
            No spam, unsubscribe anytime. View our{' '}
            <a href="/legal/privacy" className="text-primary-600 hover:text-primary-700 underline">
              Privacy Policy
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}

